import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { SHIPMENT_TRACKING_LINK_BLOG_URL } from "../../constants";
import { CurrencySelect } from "../CurrencySelect";
import { EquipmentTypeSelect } from "../EquipmentTypeSelect";
import { NewTabLink } from "../NewTabLink";
import { NumberInput } from "../NumberInput";
import { PopulateCustomerRequirementsButton } from "../PopulateCustomerRequirementsButton";

import type { QuoteFieldsCommonProps, QuoteFieldsState } from "./types";

interface Props extends QuoteFieldsCommonProps<QuoteFieldsState> {
  onPopulateShipmentRequirements: VoidFunction;
}

export function SingleShipmentQuoteFields(props: Props) {
  const {
    shipment,
    formState,
    onValueChanged,
    audience,
    messages,
    loading,
    onSubmit,
    submitLabel,
    enableSubmit = true,
    onPopulateShipmentRequirements,
    secondaryAction,
  } = props;

  const onEquipmentTypeChanged = (newValue: string) => {
    onValueChanged<string>("equipmentType", newValue);
  };
  const onCurrencyChanged = (newValue: string) => {
    onValueChanged<string>("currency", newValue);
  };

  const unitOfMeasure = formatQuotingUnitOfMeasure(
    shipment.info.quoting_unit_of_measure,
    false,
  );
  // A carrier is quoting a price; a shipper is transcribing one they were quoted,
  // so the carrier's "your quotes are all-in" phrasing does not fit them.
  const priceHelperText =
    audience === "carrier"
      ? `Quotes are
          ${unitOfMeasure}
          (exclusive of tax, unless you explicitly state otherwise).`
      : `The rate is ${unitOfMeasure} (exclusive of tax, unless the carrier stated otherwise).`;

  return (
    <>
      <Grid container rowSpacing={2} columnSpacing={1} alignItems="flex-start">
        <Grid item xs={12} sm={6}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <PopulateCustomerRequirementsButton
              onClick={onPopulateShipmentRequirements}
              source={audience}
              enableText={false}
              sx={{ width: "3.35rem", height: "3.35rem" }}
            />
            <NumberInput
              inputLabel={formatQuotingUnitOfMeasure(
                shipment.info.quoting_unit_of_measure,
              )}
              maxDecimals={2}
              min={0}
              value={formState.price}
              onChange={(val) => onValueChanged<number | null>("price", val)}
              startAdornment={
                <InputAdornment position="start">$</InputAdornment>
              }
              helperText={priceHelperText}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <CurrencySelect
            onChange={onCurrencyChanged}
            value={formState.currency}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id="quote-pickup-eta"
            label="Pickup ETA"
            type="date"
            value={formState.pickupStart}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) =>
              onValueChanged<string>("pickupStart", e.target.value)
            }
            sx={{ width: "100%" }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id="quote-delivery-eta"
            label="Delivery ETA"
            type="date"
            value={formState.deliveryEta}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) =>
              onValueChanged<string>("deliveryEta", e.target.value)
            }
            sx={{ width: "100%" }}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <EquipmentTypeSelect
            onChange={onEquipmentTypeChanged}
            value={formState.equipmentType}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            sx={{ width: "100%" }}
            onChange={(e) => onValueChanged<string>("notes", e.target.value)}
            label="Notes (Optional)"
            value={formState.notes}
          />
        </Grid>
      </Grid>
      <Stack direction="row" mt="1rem" alignItems="center">
        <Checkbox
          checked={formState.trackingLinkOffered}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onValueChanged<boolean>("trackingLinkOffered", event.target.checked)
          }
        />
        <Typography>
          {audience === "carrier"
            ? "I can provide a tracking link for this shipment. "
            : "The carrier can provide a tracking link for this shipment. "}
          <NewTabLink href={SHIPMENT_TRACKING_LINK_BLOG_URL}>
            What are tracking links?
          </NewTabLink>
        </Typography>
      </Stack>
      {messages}
      <Stack sx={{ mt: "1.5rem" }}>
        {!loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              sx={{ p: "1rem" }}
              onClick={onSubmit}
              variant="contained"
              disabled={!enableSubmit}
            >
              {submitLabel}
            </Button>
            {secondaryAction}
          </Stack>
        ) : (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        )}
      </Stack>
    </>
  );
}
